import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../components/CustomButton';
import InfoTooltip from '../../../components/InfoTooltip';
import {
    useGetVoucherLiabilityQuery,
    useLazyGetVoucherSalesQuery,
} from '../../../api/voucher/voucherApiSlice';
import { LiabilityRollForward } from '../../../api/voucher/voucherApiTypes';
import { stationCalendarDay } from '../../../utils/stationTime';
import { formatDate } from '../../../utils';
import { cn } from '../../../utils/utils';
import { downloadCsv } from '../../../utils/csvDownload';
import { buildVoucherSalesCsv, voucherSalesCsvFileName } from '../../../utils/voucherSalesCsv';
import { getApiErrorMessage } from '../../../utils/apiError';

interface VoucherLiabilityReportProps {
    naLabel: string;
    formatMoney: (value: number | null, currency: string | null) => string;
}

const headerCell = 'text-left px-2 md:px-3 py-2 text-foreground/60 font-medium whitespace-nowrap';
const cell = 'px-2 md:px-3 py-2 text-foreground/80 whitespace-nowrap';
// What happened between the opening and the closing balance.
const MOVEMENTS: (keyof LiabilityRollForward)[] = [
    'sold', 'redeemed', 'refunded', 'writtenOff', 'expired', 'chargedBack',
];
// Endbestand stays in view while the movements scroll beneath it: on a phone in
// landscape the full table is wider than the screen, and the closing balance is
// the figure the report is read for.
// The divider marks the edge the scrolled columns slide under.
const closingCell = 'sticky right-0 bg-text-contrast shadow-[inset_1px_0_0_rgba(var(--color-foreground),0.12)]';

/**
 * The movement columns on a narrow screen: hidden behind a "…" column until
 * asked for (phone in portrait shows Gekauft · Anfangsbestand · … ·
 * Endbestand); always shown from `md` up.
 */
const movementClass = (expanded: boolean): string => (expanded ? '' : 'hidden md:table-cell');
const dotsClass = (expanded: boolean): string => (expanded ? 'hidden' : 'md:hidden');

/**
 * The prepaid-voucher liability as of a day, with its roll-forward since the
 * end of the previous year by purchase year, and the year's sales with their
 * VAT as a CSV (#3261; ADR-0002 amendment, decision 7). Figures are the
 * backend's (`prepaidVoucherState`) — the same module as the total above.
 */
const VoucherLiabilityReport = ({ naLabel, formatMoney }: VoucherLiabilityReportProps) => {
    const { t, i18n } = useTranslation();
    const dateLanguage: 'de' | 'en' = i18n.language === 'de' ? 'de' : 'en';
    // Narrow screens only: the movements between opening and closing balance.
    const [showMovements, setShowMovements] = useState(false);
    const [asOf, setAsOf] = useState<string>(() => stationCalendarDay(new Date()));
    const { data, isFetching, isError, error } = useGetVoucherLiabilityQuery(asOf, { refetchOnMountOrArgChange: true });
    const [loadSales, { isFetching: salesLoading }] = useLazyGetVoucherSalesQuery();
    const [salesError, setSalesError] = useState<string | null>(null);
    const year = asOf.slice(0, 4);

    const downloadSales = async () => {
        setSalesError(null);
        try {
            const report = await loadSales(year).unwrap();
            downloadCsv(
                voucherSalesCsvFileName(t('vouchers_codes.liability.sales_file'), year),
                buildVoucherSalesCsv(report, {
                    date: t('vouchers_codes.liability.sales.date'),
                    code: t('vouchers_codes.columns.code'),
                    gross: t('vouchers_codes.liability.sales.gross'),
                    currency: t('vouchers_codes.liability.sales.currency'),
                    vatCountry: t('vouchers_codes.liability.sales.vat_country'),
                    vatRate: t('vouchers_codes.liability.sales.vat_rate'),
                    vatAmount: t('vouchers_codes.liability.sales.vat_amount'),
                    refunded: t('vouchers_codes.prepaid_list.refunded'),
                    status: t('vouchers_codes.prepaid_list.status_column'),
                    missing: t('common.missing'),
                    statusLabel: (s) => t(`vouchers_codes.prepaid_list.status.${s}`),
                }),
            );
        } catch (err) {
            setSalesError(getApiErrorMessage(err) ?? t('vouchers_codes.liability.sales_failed'));
        }
    };

    const money = (n: number) => formatMoney(n, data?.currency ?? null);

    return (
        <div className={'bg-text-contrast rounded-[5px] shadow-sm p-4 mt-6'}>
            <div className={'flex flex-wrap items-end gap-4 mb-3'}>
                <h2 className={'text-base font-semibold text-foreground flex items-center gap-1.5'}>
                    {t('vouchers_codes.liability.title')}
                    <InfoTooltip text={t('vouchers_codes.liability.help')} />
                </h2>
                <label className={'text-sm text-foreground/60 flex items-center gap-2'}>
                    {t('vouchers_codes.liability.as_of')}
                    <input
                        type={'date'}
                        value={asOf}
                        onChange={(e) => e.target.value && setAsOf(e.target.value)}
                        className={'border border-foreground/20 rounded px-2 py-1 text-foreground'}
                    />
                </label>
                <CustomButton
                    title={t('vouchers_codes.liability.download_sales', { year })}
                    isFilled={false}
                    onClick={downloadSales}
                    disabled={salesLoading}
                />
            </div>
            {salesError && <p className={'text-sm text-red-500 mb-2'}>{salesError}</p>}
            {isError && (
                <p className={'text-sm text-foreground/60'}>
                    {getApiErrorMessage(error) ?? t('vouchers_codes.liability.load_failed')}
                </p>
            )}
            {!isError && (isFetching || !data) && <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>}
            {!isError && !isFetching && data && (
                <div>
                    <p className={'text-sm text-foreground/60 mb-2'}>
                        {t('vouchers_codes.liability.period', {
                            // #3332 D6: dates as the reader writes them (31.12.2025), not ISO.
                            from: formatDate(data.from, dateLanguage),
                            to: formatDate(data.asOf, dateLanguage),
                        })}
                    </p>
                    <button
                        type={'button'}
                        className={'md:hidden text-sm text-primary mb-2'}
                        onClick={() => setShowMovements((v) => !v)}
                    >
                        {t(showMovements ? 'vouchers_codes.liability.hide_movements' : 'vouchers_codes.liability.show_movements')}
                    </button>
                    {/* Only the table scrolls sideways; the period line and toggle stay put. */}
                    <div className={'overflow-x-auto'}>
                        <table className={'w-full text-sm'}>
                            <thead>
                                <tr className={'border-b border-foreground/10'}>
                                    <th className={headerCell}>{t('vouchers_codes.liability.purchase_year')}</th>
                                    <th className={headerCell}>{t('vouchers_codes.liability.columns.opening')}</th>
                                    <th className={cn(headerCell, dotsClass(showMovements))}>
                                        <button
                                            type={'button'}
                                            aria-label={t('vouchers_codes.liability.show_movements')}
                                            onClick={() => setShowMovements(true)}
                                        >
                                        …
                                        </button>
                                    </th>
                                    {MOVEMENTS.map((c) => (
                                        <th key={c} className={cn(headerCell, movementClass(showMovements))}>
                                            {t(`vouchers_codes.liability.columns.${c}`)}
                                        </th>
                                    ))}
                                    <th className={cn(headerCell, closingCell)}>{t('vouchers_codes.liability.columns.closing')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ...data.byPurchaseYear.map((row) => ({ key: row.purchaseYear, label: row.purchaseYear, values: row, isTotal: false })),
                                    { key: 'total', label: t('vouchers_codes.liability.total'), values: data.total, isTotal: true },
                                ].map((r) => (
                                    <tr key={r.key} className={r.isTotal ? 'font-semibold' : 'border-b border-foreground/5'}>
                                        <td className={cell}>{r.label}</td>
                                        <td className={cell}>{money(r.values.opening)}</td>
                                        <td className={cn(cell, dotsClass(showMovements))}>…</td>
                                        {MOVEMENTS.map((c) => (
                                            <td key={c} className={cn(cell, movementClass(showMovements))}>{money(r.values[c])}</td>
                                        ))}
                                        <td className={cn(cell, closingCell)}>{money(r.values.closing)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {data.withoutPayment.length > 0 && (
                        <p className={'text-sm text-foreground/60 mt-3'}>
                            {t('vouchers_codes.liability.without_payment', {
                                codes: data.withoutPayment.map((w) => w.code ?? naLabel).join(', '),
                            })}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default VoucherLiabilityReport;
